import { z } from "@reflag/shared/utils/zod";

import { APIEmptyResponse, APIResponse } from "./api";
import { redirectUrlSchema } from "./schemas/redirectUrlSchema";

export interface GitHubConnection {
  id: string;
  createdAt: string;
  updatedAt: string;
  repository: string | null;
  codeReferencesSyncError: CodeReferencesSyncError | null;
}

export const CodeReferencesSyncErrorCodeSchema = z.enum([
  "empty_repository",
  "repository_too_large",
  "unknown_error",
]);

export type CodeReferencesSyncErrorCode = z.infer<
  typeof CodeReferencesSyncErrorCodeSchema
>;

export interface CodeReferencesSyncError {
  code: CodeReferencesSyncErrorCode;
  message: string;
}

export const ConnectToGitHubQuerySchema = z
  .object({
    redirectUri: redirectUrlSchema.optional(),
    installation_id: z.string().optional(),
    code: z.string().optional(),
  })
  .strict();

export type ConnectToGitHubQueryType = z.infer<
  typeof ConnectToGitHubQuerySchema
>;

export const CallbackAppQuerySchema = z.object({
  installation_id: z.coerce.number().min(1),
});

export type CallbackAppQueryType = z.infer<typeof CallbackAppQuerySchema>;

export const CallbackUserQuerySchema = z.object({
  state: z.string().optional(),
  installation_id: z.coerce.number().optional(),
  orgId: z.string().optional(),
  code: z.string(),
  setup_action: z.string(),
});

export type CallbackUserQueryType = z.infer<typeof CallbackUserQuerySchema>;

export const GithubIntegrationSchema = z
  .object({
    repository: z.string().max(255),
  })
  .strict();

export type GithubRepo = {
  id: number;
  name: string;
  fullName: string;
  private: boolean;
  url: string;
  description: string | null;
  owner: {
    login: string;
    avatarUrl: string;
  } | null;
};

export type GithubIntegrationType = z.infer<typeof GithubIntegrationSchema>;

export interface GitHubConnectionAPI {
  "/orgs/:orgId/github/connection": {
    GET: {
      response: APIResponse<GitHubConnection | null>;
      params: { orgId: string };
    };
    DELETE: {
      response: APIEmptyResponse;
      params: { orgId: string };
    };
    POST: {
      response: APIResponse<GitHubConnection>;
      params: { orgId: string };
      body: GithubIntegrationType;
    };
  };
  "/orgs/:orgId/github/connection/repos": {
    GET: {
      response: APIResponse<GithubRepo[]>;
      params: { orgId: string };
    };
  };
  "/orgs/:orgId/github/connect": {
    GET: {
      params: { orgId: string };
      query: ConnectToGitHubQueryType;
    };
  };
  "/callback/github": {
    GET: {
      response: void;
      query: CallbackAppQueryType;
    };
  };
  "/callback/github-user": {
    GET: {
      response: void;
      query: CallbackUserQueryType;
    };
  };
}
